import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  SportsScore as MatchIcon,
} from "@mui/icons-material";
import { keyframes } from "@mui/material/styles";
import axios from "axios";
import { API_BASE_URL } from "../config/api";

interface HeadToHeadDialogProps {
  open: boolean;
  onClose: () => void;
  player1Id: string;
  player2Id: string;
  player1Name: string;
  player2Name: string;
  // Season currently loaded on the page; enables the "This season" scope
  seasonId?: string | null;
}

interface HeadToHeadStats {
  player1: { id: string; name: string };
  player2: { id: string; name: string };
  player1Wins: number;
  player2Wins: number;
  draws: number;
  totalMatches: number;
  matches: Array<{
    id: string;
    date: string | null;
    seasonId: string | null;
    seasonName: string | null;
    division: string | null;
    player1IsGuest: boolean;
    player2IsGuest: boolean;
    team1Score: number;
    team2Score: number;
    player1Team: "team1" | "team2";
    pair1Players: string;
    pair2Players: string;
  }>;
}

const ballTravel = keyframes`
  0%   { left: 8%;  transform: translateY(0); }
  25%  { transform: translateY(-34px); }
  50%  { left: 88%; transform: translateY(0); }
  75%  { transform: translateY(-34px); }
  100% { left: 8%;  transform: translateY(0); }
`;
const ballShadow = keyframes`
  0%, 50%, 100% { transform: scale(1); opacity: 0.35; }
  25%, 75%      { transform: scale(0.6); opacity: 0.15; }
`;
const hitPulse = keyframes`
  0%, 45%, 100% { transform: scale(1); }
  50%           { transform: scale(1.12); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const LOADING_MESSAGES = [
  "Dusting off the match records…",
  "Counting every point of the rivalry…",
  "Checking who really owns the bragging rights…",
  "Asking the net who was closer…",
  "Replaying the golden points…",
];

// Padel-style loading: a ball rallies between the two players while messages rotate.
function H2HLoading({ player1Name, player2Name }: { player1Name: string; player2Name: string }) {
  const [msgIndex, setMsgIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setMsgIndex((i) => (i + 1) % LOADING_MESSAGES.length), 2200);
    return () => clearInterval(id);
  }, []);

  const nameSx = (delay: string) => ({
    fontWeight: 700,
    animation: `${hitPulse} 1.6s ease-in-out infinite`,
    animationDelay: delay,
  });

  return (
    <Box py={3} textAlign="center">
      <Box display="flex" justifyContent="space-between" alignItems="center" px={1}>
        <Typography sx={nameSx("0s")}>{player1Name}</Typography>
        <Typography sx={nameSx("0.8s")}>{player2Name}</Typography>
      </Box>
      <Box sx={{ position: "relative", height: 70, mt: 1 }}>
        {/* net */}
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            bottom: 8,
            width: 2,
            height: 36,
            bgcolor: "divider",
            transform: "translateX(-50%)",
          }}
        />
        {/* ground line */}
        <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 6, height: 2, bgcolor: "divider" }} />
        {/* ball shadow */}
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            bottom: 2,
            width: 18,
            height: 5,
            borderRadius: "50%",
            bgcolor: "text.primary",
            ml: "-9px",
            animation: `${ballShadow} 1.6s ease-in-out infinite`,
          }}
        />
        {/* ball */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            width: 18,
            height: 18,
            ml: "-9px",
            borderRadius: "50%",
            bgcolor: "#d4e157",
            border: "2px solid #c0ca33",
            animation: `${ballTravel} 3.2s linear infinite`,
          }}
        />
      </Box>
      <Typography
        key={msgIndex}
        variant="body2"
        color="text.secondary"
        sx={{ mt: 1, minHeight: 24, animation: `${fadeIn} 0.5s ease` }}
      >
        {LOADING_MESSAGES[msgIndex]}
      </Typography>
    </Box>
  );
}

function HeadToHeadDialog({
  open,
  onClose,
  player1Id,
  player2Id,
  player1Name,
  player2Name,
  seasonId,
}: HeadToHeadDialogProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<HeadToHeadStats | null>(null);
  const [scope, setScope] = useState<"season" | "all">("all");

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;

  useEffect(() => {
    if (open) setScope("all");
  }, [open]);

  useEffect(() => {
    setPage(1);
  }, [open, scope, player1Id, player2Id]);

  useEffect(() => {
    if (open && player1Id && player2Id) {
      fetchHeadToHead();
    }
  }, [open, player1Id, player2Id]);

  const fetchHeadToHead = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${API_BASE_URL}/api/players/${player1Id}/head-to-head/${player2Id}`
      );
      setStats(response.data);
    } catch (err: any) {
      console.error("Error fetching head-to-head:", err);
      setError(
        err.response?.data?.error || "Failed to fetch head-to-head stats"
      );
    } finally {
      setLoading(false);
    }
  };

  const getWinPercentage = (wins: number, total: number) => {
    if (total === 0) return 0;
    return ((wins / total) * 100).toFixed(1);
  };

  // Tally the selected scope client-side from the full match list
  const scopedMatches = (stats?.matches ?? []).filter(
    (m) => scope === "all" || !seasonId || m.seasonId === seasonId
  );
  const scoped = scopedMatches.reduce(
    (acc, m) => {
      if (m.team1Score === m.team2Score) acc.draws++;
      else if ((m.team1Score > m.team2Score) === (m.player1Team === "team1")) acc.player1Wins++;
      else acc.player2Wins++;
      return acc;
    },
    { player1Wins: 0, player2Wins: 0, draws: 0 }
  );
  const scopedTotal = scopedMatches.length;

  const GuestChip = () => (
    <Chip label="Guest" size="small" color="warning" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: "0.65rem" }} />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <MatchIcon color="primary" />
          <Typography variant="h6">Head-to-Head Record</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <H2HLoading player1Name={player1Name} player2Name={player2Name} />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : stats ? (
          <Box>
            {seasonId && (
              <Box display="flex" justifyContent="center" mb={2}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={scope}
                  onChange={(_, v) => v && setScope(v)}
                >
                  <ToggleButton value="season">This season</ToggleButton>
                  <ToggleButton value="all">All seasons</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Player Names */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Typography variant="h6" fontWeight="bold">
                {player1Name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                vs
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {player2Name}
              </Typography>
            </Box>

            {/* Win Record */}
            <Box display="flex" justifyContent="space-around" mb={3}>
              <Box textAlign="center">
                <Typography variant="h3" color="primary" fontWeight="bold">
                  {scoped.player1Wins}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Wins
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h3" color="text.secondary">
                  {scoped.draws}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Draws
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h3" color="primary" fontWeight="bold">
                  {scoped.player2Wins}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Wins
                </Typography>
              </Box>
            </Box>

            {/* Win Percentage */}
            <Box display="flex" justifyContent="space-around" mb={3}>
              <Typography variant="body2" color="text.secondary">
                {getWinPercentage(scoped.player1Wins, scopedTotal)}% Win
                Rate
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getWinPercentage(scoped.player2Wins, scopedTotal)}% Win
                Rate
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Total Matches */}
            <Box textAlign="center" mb={2}>
              <Chip
                icon={<TrophyIcon />}
                label={`${scopedTotal} Total Match${
                  scopedTotal !== 1 ? "es" : ""
                }`}
                color="primary"
                variant="outlined"
              />
            </Box>

            {/* Recent Matches */}
            {scopedMatches.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  Matches (latest first)
                </Typography>
                {scopedMatches.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((match) => {
                  const player1Won =
                    (match.player1Team === "team1" &&
                      match.team1Score > match.team2Score) ||
                    (match.player1Team === "team2" &&
                      match.team2Score > match.team1Score);
                  const player2Won =
                    !player1Won && match.team1Score !== match.team2Score;

                  // Determine which pair each player was in
                  const player1Pair =
                    match.player1Team === "team1"
                      ? match.pair1Players
                      : match.pair2Players;
                  const player2Pair =
                    match.player1Team === "team1"
                      ? match.pair2Players
                      : match.pair1Players;

                  return (
                    <Box
                      key={match.id}
                      display="flex"
                      flexDirection="column"
                      py={1.5}
                      px={2}
                      mb={1}
                      sx={{
                        backgroundColor: "action.hover",
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box flex={1}>
                          <Typography
                            variant="body2"
                            fontWeight={player1Won ? "bold" : "normal"}
                            color={player1Won ? "success.main" : "text.primary"}
                            sx={{ fontSize: "0.85rem" }}
                          >
                            {player1Pair}
                            {player1Won && " ✓"}
                          </Typography>
                        </Box>
                        <Box textAlign="center" px={2}>
                          <Typography variant="body2" fontWeight="bold">
                            {match.player1Team === "team1"
                              ? `${match.team1Score} - ${match.team2Score}`
                              : `${match.team2Score} - ${match.team1Score}`}
                          </Typography>
                        </Box>
                        <Box flex={1} textAlign="right">
                          <Typography
                            variant="body2"
                            fontWeight={player2Won ? "bold" : "normal"}
                            color={player2Won ? "success.main" : "text.primary"}
                            sx={{ fontSize: "0.85rem" }}
                          >
                            {player2Won && "✓ "}
                            {player2Pair}
                          </Typography>
                        </Box>
                      </Box>
                      <Box textAlign="center" mt={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          {[
                            match.date ? new Date(match.date).toLocaleDateString() : null,
                            scope === "all" ? match.seasonName : null,
                            match.division,
                          ].filter(Boolean).join(" · ")}
                          {match.player1IsGuest && (
                            <> <GuestChip /> <span>{player1Name}</span></>
                          )}
                          {match.player2IsGuest && (
                            <> <GuestChip /> <span>{player2Name}</span></>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
                {scopedMatches.length > PAGE_SIZE && (
                  <Box display="flex" justifyContent="center" mt={1}>
                    <Pagination
                      size="small"
                      count={Math.ceil(scopedMatches.length / PAGE_SIZE)}
                      page={page}
                      onChange={(_, v) => setPage(v)}
                    />
                  </Box>
                )}
              </Box>
            )}

            {scopedTotal === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                These players haven't faced each other yet!
              </Alert>
            )}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default HeadToHeadDialog;
